import { resolveCategoryImagePath } from '@susu/order-list'

import type { ActiveOrder, ActiveOrderProduct } from '../api/activeOrdersApi'

/**
 * Pure mapping from an `active_orders` card to what the Active Cards render.
 *
 * Copy is flow-dependent (RP-5243/5244 order, RP-5246/5247 custom made,
 * RP-5248/5249 alterations): the same MAO status shows different wording, and
 * alterations use relative dates ("Finished today") where the other flows use
 * absolute ones ("Delivery by 22 November").
 *
 * Returns and exchanges (data contract V2) are a second axis on top of that:
 * they are ordinary rows discriminated only by `return_type`, and they run
 * their own two-leg status sequence. Every branch here checks the row type
 * first, because `On Its Way`/`Delivered` are deliberately shared between a
 * regular order and an exchange's outbound leg.
 */

/** Which of the three Active Card flows a trait row belongs to. */
export type ActiveOrderFlow = 'order' | 'customMade' | 'alteration'

/**
 * Whether the row is a purchase, a refund, or an exchange. Derived from
 * `return_type` alone — the contract ships no `card_type` field.
 */
export type ActiveOrderRowType = 'order' | 'return' | 'exchange'

/**
 * Position on the order timeline (3 steps), the alteration timeline (2 steps),
 * the inbound leg of a return/exchange (2 steps) or the exchange's outbound
 * leg (3 steps, sharing `shipped` and `delivered` with the order timeline).
 *
 * Mirrors `TimelineStep` in @susu/order-list, which renders these.
 */
export type ActiveOrderStep =
  | 'ordered'
  | 'shipped'
  | 'delivered'
  | 'being-altered'
  | 'ready'
  | 'registered'
  | 'received'
  | 'exchanged'

export type ActiveOrderTimelineVariant =
  | 'order'
  | 'alteration'
  | 'return'
  | 'exchange'

const CUSTOM_MADE_ITEM_TYPES = new Set(['custom made', 'custom made lite'])

/** Trait statuses that mean "the alteration workshop has the garment". */
const ALTERATION_STATUSES = new Set(['alterations in progress'])

/** MAO 2000 equivalents — worded per flow ("Preparing order" vs "In production"). */
const IN_PREPARATION_STATUSES = new Set([
  'order confirmed',
  'preparing order',
  'in production',
])

const ON_ITS_WAY_STATUSES = new Set(['on its way'])

/** V2 return/exchange statuses. */
const RETURN_REGISTERED_STATUSES = new Set(['return registered'])
/**
 * The garment is back with us. One card design for all three, differing only
 * in the title: `Return Received` names the warehouse acknowledging the
 * parcel, `Processing Return` the refund being worked on.
 */
const PROCESSING_STATUSES = new Set([
  'return received',
  'processing return',
  'processing exchange',
])
const RETURN_RECEIVED_STATUSES = new Set(['return received'])
const EXCHANGE_CONFIRMED_STATUSES = new Set(['exchange confirmed'])

/**
 * `return_type` values that mean a replacement is coming back out: MAO's own
 * two, plus the three the producer derives from the money direction once the
 * garment is back (RP-6194).
 */
const EXCHANGE_RETURN_TYPES = new Set([
  'even exchange',
  'uneven exchange',
  'exchange',
  'exchange with refund',
  'exchange with payment',
])

/**
 * `return_type` values where money is coming back, so a Return Received card
 * may promise a refund window. MAO's two exchange types stay in only for the
 * release gap until the producer derives the direction (RP-6194); once no
 * received card carries them any more they can go. `Exchange` and
 * `Exchange with payment` owe the customer nothing, and an unrecognised value
 * is not a promise we can make.
 */
const REFUND_SUBTITLE_RETURN_TYPES = new Set([
  'refund',
  'exchange with refund',
  'even exchange',
  'uneven exchange',
])
const READY_FOR_PICKUP_STATUSES = new Set(['ready for pickup'])
const DELIVERED_STATUSES = new Set(['delivered', 'purchased', 'picked-up'])
/** The trait sends this once a pick-up point parcel has been collected. */
const PICKED_UP_STATUSES = new Set(['picked-up'])

const PICKUP_DELIVERY_METHODS = new Set(['shiptostore', 'pickupatstore'])
const STORE_DELIVERY_LOCATIONS = new Set(['store'])
const PICKUP_POINT_DELIVERY_LOCATIONS = new Set([
  'pick-up point',
  'pickup point',
])
const NEXT_DOOR_DELIVERY_LOCATIONS = new Set(['next door'])

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/

const MS_PER_DAY = 24 * 60 * 60 * 1000

function normalize(value: string | null | undefined): string {
  return value?.trim().toLowerCase() ?? ''
}

export function isCustomMadeProduct(product: ActiveOrderProduct): boolean {
  return CUSTOM_MADE_ITEM_TYPES.has(normalize(product.item_type))
}

/** Whether any garment on the card is Custom Made — drives the image badge. */
export function hasCustomMadeProduct(order: ActiveOrder): boolean {
  return (order.products ?? []).some(isCustomMadeProduct)
}

/**
 * Refund vs exchange vs plain purchase. Anything non-empty that isn't an
 * exchange is treated as a return: it is a MAO return order either way, and a
 * value we don't recognise should still not render as a purchase.
 */
export function getActiveOrderRowType(order: ActiveOrder): ActiveOrderRowType {
  const returnType = normalize(order.return_type)

  if (!returnType) return 'order'

  return EXCHANGE_RETURN_TYPES.has(returnType) ? 'exchange' : 'return'
}

/** Whether the row is a return or an exchange rather than a purchase. */
export function isActiveOrderReturnOrExchange(order: ActiveOrder): boolean {
  return getActiveOrderRowType(order) !== 'order'
}

/**
 * Alterations win over custom made: an alteration card is about the workshop
 * step, whatever the garment underneath was.
 */
export function getActiveOrderFlow(order: ActiveOrder): ActiveOrderFlow {
  if (
    ALTERATION_STATUSES.has(normalize(order.fulfillment_status)) ||
    order.alteration_delivery_promise
  ) {
    return 'alteration'
  }

  return hasCustomMadeProduct(order) ? 'customMade' : 'order'
}

function isStoreCollection(order: ActiveOrder): boolean {
  return (
    STORE_DELIVERY_LOCATIONS.has(normalize(order.delivery_location)) ||
    PICKUP_DELIVERY_METHODS.has(normalize(order.delivery_method))
  )
}

/**
 * Return/exchange titles, keyed per row type rather than interpolating the
 * noun. Dutch and Chinese inflect the surrounding words, so "Processing
 * {return}" would not survive translation as one message.
 */
const RETURN_TITLE_KEYS: Record<
  'registered' | 'received' | 'processing',
  Record<'return' | 'exchange', string>
> = {
  registered: {
    return: 'activeOrders.status.returnRegistered',
    exchange: 'activeOrders.status.exchangeRegistered',
  },
  // `Return Received` is a refund-side status; an exchange row carrying it
  // keeps the exchange wording, since the customer is waiting on a replacement.
  received: {
    return: 'activeOrders.status.returnReceived',
    exchange: 'activeOrders.status.processingExchange',
  },
  // A refund shows no date past registration (RP-5949) — "Reviewing return"
  // until the card drops off. An exchange keeps its own sequence.
  processing: {
    return: 'activeOrders.status.reviewingReturn',
    exchange: 'activeOrders.status.processingExchange',
  },
}

/**
 * Title key for a return/exchange row. `null` for a status outside the V2
 * sequence, so the caller falls back to the raw trait string.
 */
function getReturnTitleKey(
  order: ActiveOrder,
  rowType: 'return' | 'exchange',
): string | null {
  const status = normalize(order.fulfillment_status)

  if (RETURN_REGISTERED_STATUSES.has(status)) {
    return RETURN_TITLE_KEYS.registered[rowType]
  }
  // Before the broader processing check, which this status is also part of:
  // the two share every other trait of the card, only the title differs.
  if (RETURN_RECEIVED_STATUSES.has(status)) {
    return RETURN_TITLE_KEYS.received[rowType]
  }
  if (PROCESSING_STATUSES.has(status)) {
    return RETURN_TITLE_KEYS.processing[rowType]
  }
  if (EXCHANGE_CONFIRMED_STATUSES.has(status)) {
    return 'activeOrders.status.exchangeConfirmed'
  }
  // The replacement's outbound leg reuses the order copy verbatim.
  if (ON_ITS_WAY_STATUSES.has(status)) return 'activeOrders.status.onItsWay'
  if (DELIVERED_STATUSES.has(status)) return 'activeOrders.status.delivered'

  return null
}

/**
 * MAO 7500 splits four ways on where the parcel ended up. A pick-up point
 * parcel has two delivered states: waiting at the point, then collected.
 */
function getDeliveredTitleKey(order: ActiveOrder): string {
  const location = normalize(order.delivery_location)
  const status = normalize(order.fulfillment_status)

  if (isStoreCollection(order)) return 'activeOrders.status.readyForPickup'
  if (NEXT_DOOR_DELIVERY_LOCATIONS.has(location)) {
    return 'activeOrders.status.deliveredNextDoor'
  }
  if (PICKUP_POINT_DELIVERY_LOCATIONS.has(location)) {
    return PICKED_UP_STATUSES.has(status)
      ? 'activeOrders.status.pickedUpAtPickup'
      : 'activeOrders.status.deliveredAtPickup'
  }

  return 'activeOrders.status.delivered'
}

/**
 * Translation key for the card title. Returns `null` when the trait sends a
 * status we have no copy for, so the caller can fall back to the raw string.
 */
export function getActiveOrderTitleKey(order: ActiveOrder): string | null {
  const status = normalize(order.fulfillment_status)
  const rowType = getActiveOrderRowType(order)

  if (rowType !== 'order') return getReturnTitleKey(order, rowType)

  const flow = getActiveOrderFlow(order)

  if (flow === 'alteration') {
    if (READY_FOR_PICKUP_STATUSES.has(status)) {
      return 'activeOrders.status.readyForPickup'
    }
    return 'activeOrders.status.alterationsInProgress'
  }

  if (IN_PREPARATION_STATUSES.has(status)) {
    // Same MAO status, different word per flow (RP-5243 vs RP-5246/5247).
    return flow === 'customMade'
      ? 'activeOrders.status.inProduction'
      : 'activeOrders.status.preparingOrder'
  }

  if (ON_ITS_WAY_STATUSES.has(status)) {
    return 'activeOrders.status.onItsWay'
  }

  if (READY_FOR_PICKUP_STATUSES.has(status)) {
    return 'activeOrders.status.readyForPickup'
  }

  if (DELIVERED_STATUSES.has(status)) return getDeliveredTitleKey(order)

  return null
}

/**
 * Parse an ISO 8601 (YYYY-MM-DD) trait date as a UTC calendar day.
 *
 * UTC on purpose: `new Date('2026-11-22')` is UTC midnight, so reading it in a
 * negative-offset timezone would land on the 21st.
 */
function parseTraitDate(isoDate: string | null | undefined): Date | null {
  const match = isoDate?.match(ISO_DATE_PATTERN)
  if (!match) return null

  const [, year, month, day] = match
  const yearNumber = Number(year)
  const monthNumber = Number(month)
  const dayNumber = Number(day)
  const date = new Date(Date.UTC(yearNumber, monthNumber - 1, dayNumber))

  // Reject overflow dates (e.g. 2026-02-31, which Date.UTC rolls into March).
  if (
    date.getUTCFullYear() !== yearNumber ||
    date.getUTCMonth() !== monthNumber - 1 ||
    date.getUTCDate() !== dayNumber
  ) {
    return null
  }

  return date
}

/**
 * Absolute day-and-month label, e.g. `22 November`, or `22 Nov` with
 * `month: 'short'`.
 *
 * English is formatted as `en-GB`: the design renders day-first, which bare
 * `en` (US ordering) would flip to "November 22". Other languages keep their
 * own `Intl` ordering, which matters for the China deployment.
 */
export function formatActiveOrderDate(
  isoDate: string | null | undefined,
  language = 'en',
  month: 'long' | 'short' = 'long',
): string | null {
  const date = parseTraitDate(isoDate)
  if (!date) return null

  const locale = language.toLowerCase().startsWith('en') ? 'en-GB' : language

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month,
    timeZone: 'UTC',
  }).format(date)
}

/** Whole days from `today` to the trait date; negative when already past. */
export function getDaysUntil(
  isoDate: string | null | undefined,
  today: Date = new Date(),
): number | null {
  const date = parseTraitDate(isoDate)
  if (!date) return null

  const todayUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  )

  return Math.round((date.getTime() - todayUtc) / MS_PER_DAY)
}

export type ActiveOrderSubtitleKind =
  | 'deliveryBy'
  | 'deliveredOn'
  | 'atStore'
  | 'alterationFinishedToday'
  | 'alterationFinishedTomorrow'
  | 'alterationFinishingIn'
  | 'registeredOn'
  | 'refundProcessingTime'

export interface ActiveOrderSubtitle {
  kind: ActiveOrderSubtitleKind
  /** Interpolation values for the matching `activeOrders.*` message. */
  params: Record<string, string>
}

function getAlterationSubtitle(
  order: ActiveOrder,
  today: Date,
): ActiveOrderSubtitle | null {
  if (READY_FOR_PICKUP_STATUSES.has(normalize(order.fulfillment_status))) {
    const store = order.store_name?.trim()
    return store ? { kind: 'atStore', params: { store } } : null
  }

  const days = getDaysUntil(order.alteration_delivery_promise, today)
  if (days === null) return null

  // Anything already due (or overdue) reads as finished today.
  if (days <= 0) return { kind: 'alterationFinishedToday', params: {} }
  if (days === 1) return { kind: 'alterationFinishedTomorrow', params: {} }

  return { kind: 'alterationFinishingIn', params: { days: String(days) } }
}

/**
 * Second line for a return/exchange row. The two "processing" states quote a
 * fixed service window rather than a date — MAO has no promise to give while
 * the garment is in the warehouse.
 */
function getReturnSubtitle(
  order: ActiveOrder,
  language: string,
): ActiveOrderSubtitle | null {
  const status = normalize(order.fulfillment_status)

  // Before the garment is back nothing is known about the money, so no state
  // before receipt promises a refund window. A registered return or exchange
  // quotes when the request was made (MAO never sends a delivery promise
  // before receipt); the two customer-care "pending approval" states show
  // nothing, see below.
  if (
    RETURN_REGISTERED_STATUSES.has(status) ||
    EXCHANGE_CONFIRMED_STATUSES.has(status)
  ) {
    const registeredOn = formatActiveOrderDate(
      order.registered_on_date,
      language,
    )
    return registeredOn
      ? { kind: 'registeredOn', params: { date: registeredOn } }
      : null
  }

  // The refund window is a promise about money, so it is shown only once the
  // garment is received and only where money comes back: an even swap, or an
  // exchange the customer paid extra for, owes them nothing (RP-6195).
  if (RETURN_RECEIVED_STATUSES.has(status)) {
    return REFUND_SUBTITLE_RETURN_TYPES.has(normalize(order.return_type))
      ? { kind: 'refundProcessingTime', params: {} }
      : null
  }

  // Awaiting customer-care approval: a refund window here was a guess the
  // customer read as money already on its way.
  if (PROCESSING_STATUSES.has(status)) return null

  if (DELIVERED_STATUSES.has(status)) {
    const deliveredOn = formatActiveOrderDate(order.delivered_on_date, language)
    return deliveredOn
      ? { kind: 'deliveredOn', params: { date: deliveredOn } }
      : null
  }

  // On its way quotes the replacement leg in flight.
  const deliveryDate = formatActiveOrderDate(order.delivery_promise, language)

  return deliveryDate
    ? { kind: 'deliveryBy', params: { date: deliveryDate } }
    : null
}

/**
 * Resolve the card's second line. Alterations get relative wording; order and
 * custom made get "Delivery by {date}" / "on {date}" / "{store}"; returns
 * and exchanges get their own registration date and service windows.
 * Returns `null` when the card has no date yet — `delivery_promise` is null
 * until the order is allocated.
 */
export function getActiveOrderSubtitle(
  order: ActiveOrder,
  language = 'en',
  today: Date = new Date(),
): ActiveOrderSubtitle | null {
  if (isActiveOrderReturnOrExchange(order)) {
    return getReturnSubtitle(order, language)
  }

  if (getActiveOrderFlow(order) === 'alteration') {
    return getAlterationSubtitle(order, today)
  }

  const status = normalize(order.fulfillment_status)

  // Collection at a store is identified by place, not by date.
  if (
    READY_FOR_PICKUP_STATUSES.has(status) ||
    (DELIVERED_STATUSES.has(status) && isStoreCollection(order))
  ) {
    const store = order.store_name?.trim()
    return store ? { kind: 'atStore', params: { store } } : null
  }

  const deliveredOn = formatActiveOrderDate(order.delivered_on_date, language)
  if (deliveredOn) {
    return { kind: 'deliveredOn', params: { date: deliveredOn } }
  }

  const deliveryDate = formatActiveOrderDate(order.delivery_promise, language)
  if (!deliveryDate) return null

  return { kind: 'deliveryBy', params: { date: deliveryDate } }
}

export interface ActiveOrderCaseDetails {
  /** Secondary then tertiary label, blanks dropped. */
  labels: string[]
  /** Formatted `d MMM` open date, or null when the trait has none. */
  openedOn: string | null
}

/** The open Customer Service case to flag on the card, or null when there is none. */
export function getActiveOrderCaseDetails(
  order: ActiveOrder,
  language = 'en',
): ActiveOrderCaseDetails | null {
  const serviceCase = order.customer_service_case
  if (!serviceCase) return null

  return {
    labels: [serviceCase.secondary_label, serviceCase.tertiary_label]
      .map((label) => label?.trim())
      .filter((label): label is string => Boolean(label)),
    openedOn: formatActiveOrderDate(
      serviceCase.opened_on_date,
      language,
      'short',
    ),
  }
}

/**
 * Statuses on the exchange's *outbound* leg. Picked by status rather than by
 * `return_type`, because that is what decides which set of step captions the
 * card shows — a Refund row never reaches these states.
 */
function isExchangeOutboundLeg(order: ActiveOrder): boolean {
  const status = normalize(order.fulfillment_status)

  return (
    EXCHANGE_CONFIRMED_STATUSES.has(status) ||
    ON_ITS_WAY_STATUSES.has(status) ||
    DELIVERED_STATUSES.has(status)
  )
}

/**
 * Which timeline the card uses: 3-step order, 2-step alteration, or one of the
 * two return/exchange legs.
 */
export function getActiveOrderTimelineVariant(
  order: ActiveOrder,
): ActiveOrderTimelineVariant {
  if (isActiveOrderReturnOrExchange(order)) {
    return isExchangeOutboundLeg(order) ? 'exchange' : 'return'
  }

  return getActiveOrderFlow(order) === 'alteration' ? 'alteration' : 'order'
}

/** Where a return/exchange row sits on whichever leg is in flight. */
function getReturnStep(order: ActiveOrder): ActiveOrderStep {
  const status = normalize(order.fulfillment_status)

  if (EXCHANGE_CONFIRMED_STATUSES.has(status)) return 'exchanged'
  if (ON_ITS_WAY_STATUSES.has(status)) return 'shipped'
  if (DELIVERED_STATUSES.has(status)) return 'delivered'
  if (PROCESSING_STATUSES.has(status)) return 'received'

  // Registered, cancelled and anything unknown stay at the start of the
  // inbound leg; the last two hide the timeline anyway.
  return 'registered'
}

/** Where the card sits on its timeline. */
export function getActiveOrderStep(order: ActiveOrder): ActiveOrderStep {
  const status = normalize(order.fulfillment_status)

  if (isActiveOrderReturnOrExchange(order)) return getReturnStep(order)

  if (getActiveOrderFlow(order) === 'alteration') {
    return READY_FOR_PICKUP_STATUSES.has(status) ? 'ready' : 'being-altered'
  }

  if (DELIVERED_STATUSES.has(status)) return 'delivered'
  if (
    ON_ITS_WAY_STATUSES.has(status) ||
    READY_FOR_PICKUP_STATUSES.has(status)
  ) {
    return 'shipped'
  }

  // Unknown statuses stay at the first step.
  return 'ordered'
}

/**
 * The Full card drops its timeline once the parcel has landed — Figma shows it
 * only for the pre-arrival states. Alterations keep their 2-step timeline in
 * both states (RP-5249); returns and exchanges show their 2-step inbound bar
 * on the four inbound statuses only (RP-6203, superseding RP-5949).
 */
export function shouldShowActiveOrderTimeline(order: ActiveOrder): boolean {
  const status = normalize(order.fulfillment_status)

  // Returns and exchanges show the 2-step inbound bar (RP-6203, superseding
  // RP-5949). Named statuses rather than a negated outbound check, so a new
  // MAO status renders no bar instead of defaulting to the first step.
  if (isActiveOrderReturnOrExchange(order)) {
    return (
      RETURN_REGISTERED_STATUSES.has(status) || PROCESSING_STATUSES.has(status)
    )
  }

  if (getActiveOrderFlow(order) === 'alteration') return true

  return IN_PREPARATION_STATUSES.has(status) || ON_ITS_WAY_STATUSES.has(status)
}

export type ActiveOrderCtaKind =
  | 'trackOrder'
  | 'returnOrExchange'
  | 'bookPickupAppointment'
  | 'showQrCode'

/**
 * The label is the only action while the parcel is heading back. Once the
 * replacement lands the card behaves exactly like a delivered purchase — which
 * includes *not* offering a return when the parcel is waiting at a store or a
 * pick-up point rather than sitting with the customer.
 */
function getReturnCtaKind(order: ActiveOrder): ActiveOrderCtaKind | null {
  const status = normalize(order.fulfillment_status)

  if (RETURN_REGISTERED_STATUSES.has(status)) return 'showQrCode'
  if (DELIVERED_STATUSES.has(status)) return getDeliveredCtaKind(order)

  return null
}

/**
 * Returns are offered only once the parcel is with the customer: delivered at
 * home, or collected from a pick-up point. A parcel still waiting at a pick-up
 * point or left next door links to the carrier.
 */
function getDeliveredCtaKind(order: ActiveOrder): ActiveOrderCtaKind {
  const location = normalize(order.delivery_location)
  const status = normalize(order.fulfillment_status)

  if (isStoreCollection(order)) return 'bookPickupAppointment'
  if (PICKED_UP_STATUSES.has(status)) return 'returnOrExchange'
  if (
    NEXT_DOOR_DELIVERY_LOCATIONS.has(location) ||
    PICKUP_POINT_DELIVERY_LOCATIONS.has(location)
  ) {
    return 'trackOrder'
  }

  return 'returnOrExchange'
}

/**
 * State-driven CTA for the Full card (RP-5244/5246/5249). `null` while the
 * order is still being prepared — there is nothing to act on yet.
 */
export function getActiveOrderCtaKind(
  order: ActiveOrder,
): ActiveOrderCtaKind | null {
  const status = normalize(order.fulfillment_status)

  if (isActiveOrderReturnOrExchange(order)) return getReturnCtaKind(order)

  if (getActiveOrderFlow(order) === 'alteration') {
    return READY_FOR_PICKUP_STATUSES.has(status)
      ? 'bookPickupAppointment'
      : null
  }

  if (IN_PREPARATION_STATUSES.has(status)) return null

  if (READY_FOR_PICKUP_STATUSES.has(status)) return 'bookPickupAppointment'

  if (DELIVERED_STATUSES.has(status)) return getDeliveredCtaKind(order)

  if (ON_ITS_WAY_STATUSES.has(status)) return 'trackOrder'

  return null
}

/**
 * The date the card is *about*, as an ISO string — the same one its subtitle
 * quotes, so the visible order matches the visible dates.
 *
 * Precedence follows the subtitle's: an alteration quotes its workshop promise,
 * a registered return or exchange its registration date, a delivered parcel the day
 * it landed, and anything still moving its delivery promise.
 */
export function getActiveOrderSortDate(order: ActiveOrder): string | null {
  const status = normalize(order.fulfillment_status)

  if (getActiveOrderFlow(order) === 'alteration') {
    return order.alteration_delivery_promise ?? order.delivery_promise ?? null
  }

  if (
    isActiveOrderReturnOrExchange(order) &&
    (RETURN_REGISTERED_STATUSES.has(status) ||
      EXCHANGE_CONFIRMED_STATUSES.has(status))
  ) {
    return order.registered_on_date ?? null
  }

  return order.delivered_on_date ?? order.delivery_promise ?? null
}

/**
 * Cards oldest date first. Rows the producer has not dated yet sink to the
 * bottom rather than to the top, where a missing value would read as "soonest".
 *
 * Stable: same-date cards keep the order the trait sent them in, which is what
 * holds a split shipment's cards together.
 */
export function sortActiveOrdersByDate(orders: ActiveOrder[]): ActiveOrder[] {
  return orders
    .map((order, index) => ({
      order,
      index,
      date: getActiveOrderSortDate(order),
    }))
    .sort((a, b) => {
      if (a.date === b.date) return a.index - b.index
      if (!a.date) return 1
      if (!b.date) return -1

      // ISO 8601 dates sort correctly as plain strings.
      return a.date < b.date ? -1 : 1
    })
    .map(({ order }) => order)
}

export interface ActiveOrderProductView {
  imageUrl: string
  isCustomMade: boolean
}

/**
 * Garments with a usable image, in trait order.
 *
 * Ready-to-wear paths are rewritten to the category's canonical render — the
 * feed points at whichever view the style was shot for, which is rarely the
 * one the cards are designed around. Custom Made is exempt, matching
 * `getFirstProductImage` in `@susu/order-list`: the configured garment's
 * representation stays whatever the producer sent. Callers still prefix the
 * CDN base with `buildCdnImageUrl`.
 */
export function getActiveOrderProducts(
  order: ActiveOrder,
): ActiveOrderProductView[] {
  return (order.products ?? [])
    .map((product) => {
      const isCustomMade = isCustomMadeProduct(product)
      const rawPath = product.image_url?.trim() ?? ''

      // The rewrite synthesises a path rather than picking from a list the
      // producer vouched for, so nothing guarantees the canonical view exists
      // for a configured garment.
      return {
        imageUrl: isCustomMade ? rawPath : resolveCategoryImagePath(rawPath),
        isCustomMade,
      }
    })
    .filter((product) => Boolean(product.imageUrl))
}
